/**
 * ReportBuilder - the Reports page's Custom tab (docs/plans/crm-report-builder-search-collab-plan.md A4.2 to
 * A4.4, B4.3 to B4.7; work package R-7). One element is the whole tab:
 *
 *   <ReportBuilder initialReportId={searchParams.get('report')} />
 *
 * On the left, the saved reports (My reports and Team reports, pinned first) and the report settings
 * (what to count, the measure, up to two groupings with a time split counting as one, the dates, the
 * filters, bars or a table); on the right, the open report's name and actions (Save, Save as, Share,
 * Copy, Export, New report, Archive) over the result: the "how this counts" line, then a table with
 * bars, bars over time, or numbers only. Two columns from lg: (the left one wider at xl: and 2xl:),
 * stacked below.
 *
 * The browser never sends a query: it sends a spec of named choices (builderLogic toWireSpec) with the
 * viewer's time zone beside it, which the server checks against its catalog and runs through one of
 * four fixed functions (plan B4.3). A spec the panel knows cannot run (builderLogic specProblem) is
 * never sent; a saved report the server says no longer validates (`invalid`) shows that sentence and
 * does not run until a setting changes. Results are aggregates; runs are not audited, saves and exports
 * are, by the server (plan B4.7). A result runs when the settings change; bars or a table is display
 * only and never runs again.
 *
 * The deep link: `initialReportId` opens that saved report, and a later change of it opens the new one,
 * so the Reports page can pass `?report=` straight through. Opening, saving, copying and archiving move
 * the address to /crm/reports?tab=custom&report=<id> (or drop the id), so the address bar always links
 * to what is on screen and Back returns to the report before.
 *
 * Needs what every CRM page has: the router, AuthContext and the CRM's ConfirmProvider (CRMApp mounts
 * all three).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../../ui/EmptyState';
import { RefreshButton } from '../components/PageHeader';
import { Alert, Badge, btnGhost, useConfirm } from '../components/ui';
import { getPref, setPref, useIsCRMAdmin, useTeam } from '../lib';
import { studyInternalLabel } from '../types';
import type { StudyOption } from '../types';
import {
  archiveReport,
  copyReport,
  exportReport,
  getCatalog,
  getSavedReport,
  isReportBuilderUnavailable,
  listSavedReports,
  reportErrorMessage,
  runReport,
  saveReport,
  updateReport,
} from './api';
import {
  DEFAULT_SUBJECT,
  PINNED_PREF_KEY,
  REPORT_NAME_MAX,
  applyChoice,
  canEdit,
  countsAsLine,
  emptySpec,
  formatValue,
  isNoneKey,
  isOtherKey,
  measureOf,
  parsePinned,
  pivot,
  sameSpec,
  serializePinned,
  specProblem,
  subjectOf,
  togglePinned,
  toWireSpec,
} from './builderLogic';
import type { BuilderChange, PivotTable } from './builderLogic';
import ResultTable from './ResultTable';
import SavedReports, { ReportActions, SaveReportDialog, customReportHref } from './SavedReports';
import type { ReportBusy, SaveDialogValues, SavedListState } from './SavedReports';
import SpecPanel from './SpecPanel';
import TimeBars from './TimeBars';
import type { ReportCatalog, ReportResult, ReportSpec, SavedReport, SavedReportScope } from './types';

export { customReportHref } from './SavedReports';

export interface ReportBuilderProps {
  /** A saved report to open (the Reports page's ?report=); a later change of it opens that report. */
  initialReportId?: string | null;
}

/** My reports or Team reports, remembered per device. */
const SCOPE_PREF_KEY = 'reportBuilder.scope';
/** The most lead sources offered while typing the lead-source filter. */
const LEAD_SOURCE_SUGGESTIONS_MAX = 50;
/** The server's copy prefix (crmReportBuilder/saved.js COPY_PREFIX), for Save as's first name. */
const COPY_PREFIX = 'Copy of ';

type CatalogState =
  | { status: 'loading' }
  | { status: 'ready'; catalog: ReportCatalog }
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

interface RunState {
  status: 'idle' | 'running' | 'done' | 'error';
  /** The newest result, and the run key it answers. */
  result: ReportResult | null;
  resultKey: string | null;
  /** The last failure's sentence, and the run key it answers. */
  error: string | null;
  errorKey: string | null;
}

interface DialogState {
  mode: 'save' | 'saveAs';
  initial: SaveDialogValues;
  /** A fresh dialog each time. */
  seq: number;
}

interface Notice {
  tone: 'success' | 'error';
  text: string;
}

/** What a run depends on: the spec as sent, without the chart (bars or a table is display only). */
export const runKeyOf = (spec: ReportSpec): string => JSON.stringify({ ...toWireSpec(spec), chart: null });

/** The subject a new report starts on: Leads (the walkthrough, plan A4.3), else the catalog's first. */
function startSubject(catalog: ReportCatalog | null): string {
  if (!catalog || catalog.subjects.some((s) => s.key === DEFAULT_SUBJECT)) return DEFAULT_SUBJECT;
  return catalog.subjects[0]?.key ?? DEFAULT_SUBJECT;
}

/** The lead sources a result names (when it is grouped by lead source), for the filter's suggestions. */
function leadSourcesOf(result: ReportResult): string[] {
  const onA = result.spec.rows === 'lead_source';
  const onB = result.spec.columns === 'lead_source';
  if (!onA && !onB) return [];
  const found: string[] = [];
  for (const r of result.rows) {
    const key = onA ? r.a_key : r.b_key;
    if (key && !isNoneKey(key) && !isOtherKey(key) && !found.includes(key)) found.push(key);
  }
  return found;
}

const loadingList = (): SavedListState => ({ status: 'loading', reports: [], error: null });

/** The Custom tab of Reports: saved reports, settings and the result. */
export default function ReportBuilder({ initialReportId = null }: ReportBuilderProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isAdmin = useIsCRMAdmin();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { team, byId: teamById } = useTeam();

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // ---- the catalog ----------------------------------------------------------------------------
  const [catalogState, setCatalogState] = useState<CatalogState>({ status: 'loading' });
  const [catalogTry, setCatalogTry] = useState(0);
  const catalog = catalogState.status === 'ready' ? catalogState.catalog : null;
  const catalogRef = useRef<ReportCatalog | null>(null);
  catalogRef.current = catalog;

  // ---- the spec being built, and the saved report it belongs to ------------------------------
  const [spec, setSpec] = useState<ReportSpec>(() => emptySpec(DEFAULT_SUBJECT));
  const specRef = useRef(spec);
  specRef.current = spec;
  /** The person has changed a setting since the builder started or a new report began. */
  const touchedRef = useRef(false);

  const [report, setReport] = useState<SavedReport | null>(null);
  /** The spec as saved (what "Unsaved changes" compares against); null for a new report. */
  const [baseSpec, setBaseSpec] = useState<ReportSpec | null>(null);
  const [reportLoading, setReportLoading] = useState<boolean>(() => !!initialReportId);
  const [reportError, setReportError] = useState<string | null>(null);
  /** The report on screen or opening; set at once, before any state lands. */
  const openIdRef = useRef<string | null>(null);
  const reportRef = useRef<SavedReport | null>(null);
  reportRef.current = report;
  const loadSeq = useRef(0);
  /** Below lg: the settings show for a new report and fold away when a saved one is opened. */
  const [settingsOpen, setSettingsOpen] = useState<boolean>(() => !initialReportId);

  // ---- the saved lists, the pins and the scope ------------------------------------------------
  const [lists, setLists] = useState<Record<SavedReportScope, SavedListState>>(() => ({
    mine: loadingList(),
    team: loadingList(),
  }));
  const listSeq = useRef(0);
  const [pinned, setPinned] = useState<string[]>(() => parsePinned(getPref(PINNED_PREF_KEY, '')));
  const [scope, setScope] = useState<SavedReportScope>(() => (getPref(SCOPE_PREF_KEY, 'mine') === 'team' ? 'team' : 'mine'));

  // ---- the run --------------------------------------------------------------------------------
  const [run, setRun] = useState<RunState>({ status: 'idle', result: null, resultKey: null, error: null, errorKey: null });
  const [runTry, setRunTry] = useState(0);
  const runSeq = useRef(0);

  // ---- actions --------------------------------------------------------------------------------
  const [busy, setBusy] = useState<ReportBusy>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const dialogSeq = useRef(0);

  // ---- the studies (the study filter's list and names) ----------------------------------------
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    api
      .get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        const raw: unknown = res.data?.studies ?? res.data;
        const list = Array.isArray(raw) ? (raw as StudyOption[]) : [];
        setStudies(list.filter((s) => s && typeof s.id === 'string' && s.id));
      })
      .catch(() => {
        /* the study filter lists no studies; the rest works */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setCatalogState({ status: 'loading' });
    getCatalog()
      .then((c) => {
        if (!alive) return;
        if (c.subjects.length === 0) {
          setCatalogState({ status: 'unavailable' });
          return;
        }
        // A new report that nobody has touched starts from the served catalog's own defaults.
        if (!openIdRef.current && !touchedRef.current) setSpec(emptySpec(startSubject(c), c));
        setCatalogState({ status: 'ready', catalog: c });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setCatalogState(
          isReportBuilderUnavailable(e)
            ? { status: 'unavailable' }
            : { status: 'error', message: reportErrorMessage(e, "The report builder couldn't load. Please try again.") },
        );
      });
    return () => {
      alive = false;
    };
  }, [catalogTry]);

  const refreshLists = useCallback(() => {
    const seq = ++listSeq.current;
    const scopes: SavedReportScope[] = ['mine', 'team'];
    setLists((prev) => ({
      mine: { ...prev.mine, status: 'loading', error: null },
      team: { ...prev.team, status: 'loading', error: null },
    }));
    scopes.forEach((s) => {
      listSavedReports(s)
        .then((reports) => {
          if (!mounted.current || seq !== listSeq.current) return;
          setLists((prev) => ({ ...prev, [s]: { status: 'ready', reports, error: null } }));
        })
        .catch((e: unknown) => {
          if (!mounted.current || seq !== listSeq.current) return;
          setLists((prev) => ({
            ...prev,
            [s]: { status: 'error', reports: prev[s].reports, error: reportErrorMessage(e, "Saved reports couldn't load. Please try again.") },
          }));
        });
    });
  }, []);

  useEffect(() => {
    refreshLists();
  }, [refreshLists]);

  // ---- opening and starting reports -----------------------------------------------------------

  /** Put a saved report on screen: its spec becomes the working spec and the saved one. */
  const adopt = useCallback((saved: SavedReport, opts: { keepSpec?: boolean } = {}) => {
    const s = saved.spec ?? emptySpec(saved.subject ?? startSubject(catalogRef.current), catalogRef.current);
    loadSeq.current += 1;
    openIdRef.current = saved.id;
    setReport(saved);
    setBaseSpec(s);
    if (!opts.keepSpec) setSpec(s);
    setReportLoading(false);
    setReportError(null);
  }, []);

  const loadReport = useCallback(async (id: string) => {
    const seq = ++loadSeq.current;
    openIdRef.current = id;
    setReportLoading(true);
    setReportError(null);
    try {
      const saved = await getSavedReport(id);
      if (!mounted.current || seq !== loadSeq.current) return;
      adopt(saved);
      setSettingsOpen(false);
    } catch (e) {
      if (!mounted.current || seq !== loadSeq.current) return;
      // The report before stays on screen, still open (with no report before, the settings unfold).
      openIdRef.current = reportRef.current ? reportRef.current.id : null;
      if (!reportRef.current) setSettingsOpen(true);
      setReportLoading(false);
      setReportError(reportErrorMessage(e, "That report couldn't be opened. Please try again."));
    }
  }, [adopt]);

  const startNew = useCallback(() => {
    loadSeq.current += 1;
    openIdRef.current = null;
    touchedRef.current = false;
    setReport(null);
    setBaseSpec(null);
    setReportLoading(false);
    setReportError(null);
    setSettingsOpen(true);
    setSpec(emptySpec(startSubject(catalogRef.current), catalogRef.current));
  }, []);

  // The deep link: open the report it names; when it later names another, open that one. Acts only when
  // the prop itself changes, so a page that passes it once never closes a report opened from the list.
  const lastLinkRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const next = initialReportId || null;
    const first = lastLinkRef.current === undefined;
    if (!first && lastLinkRef.current === next) return;
    lastLinkRef.current = next;
    if (next) {
      if (next !== openIdRef.current) void loadReport(next);
    } else if (!first && openIdRef.current) {
      startNew();
    }
  }, [initialReportId, loadReport, startNew]);

  // ---- derived state ------------------------------------------------------------------------------
  const runKey = useMemo(() => runKeyOf(spec), [spec]);
  const problem = catalog ? specProblem(spec, catalog) : null;
  /** A saved report the server says no longer validates, not yet changed: shown, never run. */
  const blocked = !!(report && report.invalid && baseSpec && runKeyOf(baseSpec) === runKey);
  const dirty = !!(report && baseSpec && !sameSpec(spec, baseSpec));
  const canRun = !!catalog && !reportLoading && !problem && !blocked;
  const mayChange = canEdit(report, { userId, isAdmin });

  // ---- running --------------------------------------------------------------------------------------
  useEffect(() => {
    if (!catalog || reportLoading || problem || blocked) return;
    const seq = ++runSeq.current;
    const key = runKey;
    setRun((prev) => ({ ...prev, status: 'running', error: null, errorKey: null }));
    runReport(specRef.current)
      .then((result) => {
        if (!mounted.current || seq !== runSeq.current) return;
        setRun({ status: 'done', result, resultKey: key, error: null, errorKey: null });
      })
      .catch((e: unknown) => {
        if (!mounted.current || seq !== runSeq.current) return;
        setRun((prev) => ({
          ...prev,
          status: 'error',
          error: reportErrorMessage(e, "The report couldn't run. Please try again."),
          errorKey: key,
        }));
      });
  }, [catalog, reportLoading, problem, blocked, runKey, runTry]);

  const running = run.status === 'running';
  const runFailed = run.status === 'error' && run.errorKey === runKey;
  const current = run.result && run.resultKey === runKey ? run.result : null;
  // While a new run is out, the last result stays on screen, dimmed, so the page does not jump.
  const shown = current ?? (running ? run.result : null);
  const stale = !current && !!shown;

  useEffect(() => {
    if (!current) return;
    const found = leadSourcesOf(current);
    if (!found.length) return;
    setLeadSources((prev) => {
      const merged = [...found, ...prev.filter((s) => !found.includes(s))].slice(0, LEAD_SOURCE_SUGGESTIONS_MAX);
      return merged.length === prev.length && merged.every((s, i) => s === prev[i]) ? prev : merged;
    });
  }, [current]);

  // A teammate or recruiter id the server sent without a name is named from the team; a study from the list.
  const labelFor = useCallback(
    (grouping: string, key: string): string | null => {
      if (grouping === 'recruiter' || grouping === 'teammate') return teamById.get(key)?.name ?? null;
      if (grouping === 'study') {
        const s = studies.find((x) => x.id === key || x.slug === key);
        return s ? studyInternalLabel(s) || null : null;
      }
      return null;
    },
    [teamById, studies],
  );

  const table: PivotTable | null = useMemo(
    () => (shown ? pivot(shown, { catalog, labelFor }) : null),
    [shown, catalog, labelFor],
  );

  // ---- changes and actions ----------------------------------------------------------------------
  const onSpecChange = useCallback((change: BuilderChange) => {
    touchedRef.current = true;
    setSpec((prev) => applyChoice(prev, change, { catalog: catalogRef.current }));
  }, []);

  /** Leave unsaved changes only when the person says so. */
  const mayLeave = useCallback(async (): Promise<boolean> => {
    if (!dirty || !report) return true;
    return confirm({
      title: 'Discard your changes?',
      message: `"${report.name}" has changes that are not saved.`,
      confirmLabel: 'Discard changes',
      cancelLabel: 'Keep editing',
      danger: true,
    });
  }, [dirty, report, confirm]);

  const onOpen = useCallback(
    async (id: string) => {
      if (id === openIdRef.current && !reportError) return;
      if (!(await mayLeave())) return;
      setNotice(null);
      void loadReport(id);
      navigate(customReportHref(id));
    },
    [mayLeave, loadReport, navigate, reportError],
  );

  const onNew = useCallback(async () => {
    if (!(await mayLeave())) return;
    setNotice(null);
    const hadReport = openIdRef.current !== null;
    startNew();
    if (hadReport) navigate(customReportHref(null));
  }, [mayLeave, startNew, navigate]);

  const togglePin = useCallback(
    (id: string) => {
      const next = togglePinned(pinned, id);
      setPref(PINNED_PREF_KEY, serializePinned(next));
      setPinned(next);
    },
    [pinned],
  );

  const changeScope = useCallback((next: SavedReportScope) => {
    setScope(next);
    setPref(SCOPE_PREF_KEY, next);
  }, []);

  const openDialog = (mode: 'save' | 'saveAs') => {
    if (!canRun) return;
    dialogSeq.current += 1;
    setDialogError(null);
    setDialog({
      mode,
      seq: dialogSeq.current,
      initial:
        mode === 'saveAs' && report
          ? {
              name: `${COPY_PREFIX}${report.name}`.slice(0, REPORT_NAME_MAX).replace(/[\uD800-\uDBFF]$/, '').trim(),
              description: report.description ?? '',
              visibility: 'private',
            }
          : { name: '', description: '', visibility: 'private' },
    });
  };

  const onSave = async () => {
    if (!report) {
      openDialog('save');
      return;
    }
    if (!mayChange || !dirty || !canRun) return;
    const sent = specRef.current;
    setBusy('save');
    setNotice(null);
    try {
      const saved = await updateReport(report.id, { spec: sent });
      if (!mounted.current) return;
      adopt(saved, { keepSpec: !sameSpec(specRef.current, sent) });
      setNotice({ tone: 'success', text: `Saved "${saved.name}".` });
      refreshLists();
    } catch (e) {
      if (mounted.current) setNotice({ tone: 'error', text: reportErrorMessage(e, "The report couldn't be saved. Please try again.") });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const submitDialog = async (values: SaveDialogValues) => {
    if (!dialog) return;
    const sent = specRef.current;
    const hadReport = openIdRef.current !== null;
    setBusy(dialog.mode);
    setDialogError(null);
    try {
      const saved = await saveReport({
        name: values.name,
        description: values.description || null,
        spec: sent,
        visibility: values.visibility,
      });
      if (!mounted.current) return;
      adopt(saved, { keepSpec: !sameSpec(specRef.current, sent) });
      setDialog(null);
      navigate(customReportHref(saved.id), { replace: !hadReport });
      setNotice({
        tone: 'success',
        text: saved.visibility === 'team'
          ? `Saved "${saved.name}" and shared it with the team.`
          : `Saved "${saved.name}" to My reports.`,
      });
      refreshLists();
    } catch (e) {
      if (mounted.current) setDialogError(reportErrorMessage(e, "The report couldn't be saved. Please try again."));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const onToggleShare = async () => {
    if (!report) return;
    const toTeam = report.visibility !== 'team';
    // An admin can make a teammate's shared report private; after that only its author can open it.
    const someoneElses = !report.owner_user_id || report.owner_user_id !== userId;
    if (!toTeam) {
      const ok = await confirm({
        title: 'Stop sharing this report?',
        message: someoneElses
          ? `Only ${report.owner_name || 'its author'} will see "${report.name}" after this, under My reports. It leaves Team reports for everyone else, you included.`
          : `Teammates will no longer see "${report.name}" under Team reports. You keep it under My reports.`,
        confirmLabel: 'Stop sharing',
      });
      if (!ok) return;
    }
    setBusy('share');
    setNotice(null);
    try {
      const saved = await updateReport(report.id, { visibility: toTeam ? 'team' : 'private' });
      if (!mounted.current) return;
      if (!toTeam && someoneElses) {
        startNew();
        navigate(customReportHref(null), { replace: true });
      } else {
        adopt(saved, { keepSpec: true });
      }
      setNotice({
        tone: 'success',
        text: toTeam
          ? `"${saved.name}" is shared with the team.`
          : someoneElses
            ? `"${saved.name}" is private again: only ${saved.owner_name || 'its author'} can open it now.`
            : `"${saved.name}" is private again.`,
      });
      refreshLists();
    } catch (e) {
      if (mounted.current) setNotice({ tone: 'error', text: reportErrorMessage(e, "The report couldn't be changed. Please try again.") });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const onCopy = async () => {
    if (!report) return;
    // Settings changed on screen and not saved ride along onto the copy, still marked unsaved.
    const carry = dirty;
    setBusy('copy');
    setNotice(null);
    try {
      const copy = await copyReport(report.id);
      if (!mounted.current) return;
      adopt(copy, { keepSpec: carry });
      navigate(customReportHref(copy.id));
      setNotice({
        tone: 'success',
        text: carry
          ? `Copied to My reports as "${copy.name}". Your unsaved changes are still here: press Save to keep them.`
          : `Copied to My reports as "${copy.name}".`,
      });
      refreshLists();
    } catch (e) {
      if (mounted.current) setNotice({ tone: 'error', text: reportErrorMessage(e, "The report couldn't be copied. Please try again.") });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const onExport = async () => {
    if (!canRun) return;
    setBusy('export');
    setNotice(null);
    try {
      const fileName = await exportReport(specRef.current, report ? report.id : null, report ? report.name : null);
      if (mounted.current) setNotice({ tone: 'success', text: `Downloaded ${fileName}.` });
    } catch (e) {
      if (mounted.current) setNotice({ tone: 'error', text: reportErrorMessage(e, "The export didn't work. Please try again.") });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const onArchive = async () => {
    if (!report) return;
    const ok = await confirm({
      title: 'Archive this report?',
      message: `"${report.name}" will no longer be listed under My reports or Team reports. Its history is kept.`,
      confirmLabel: 'Archive',
      danger: true,
    });
    if (!ok) return;
    const { id, name } = report;
    setBusy('archive');
    setNotice(null);
    try {
      await archiveReport(id);
      if (!mounted.current) return;
      if (pinned.includes(id)) {
        const next = pinned.filter((p) => p !== id);
        setPref(PINNED_PREF_KEY, serializePinned(next));
        setPinned(next);
      }
      startNew();
      navigate(customReportHref(null), { replace: true });
      setNotice({ tone: 'success', text: `Archived "${name}".` });
      refreshLists();
    } catch (e) {
      if (mounted.current) setNotice({ tone: 'error', text: reportErrorMessage(e, "The report couldn't be archived. Please try again.") });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const subjectLabel = useCallback(
    (key: string | null): string | null => (key ? subjectOf(key, catalog)?.label ?? null : null),
    [catalog],
  );

  // ---- rendering ------------------------------------------------------------------------------------

  if (catalogState.status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500 dark:text-gray-400" data-rb="loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Loading the report builder...
      </div>
    );
  }
  if (catalogState.status === 'unavailable') {
    return (
      <EmptyState
        variant="card"
        icon={BarChart3}
        title="Custom reports aren't available yet"
        hint="This part of Reports hasn't reached the server yet. Try again after the next update."
        action={(
          <button type="button" onClick={() => setCatalogTry((n) => n + 1)} className={btnGhost}>
            Try again
          </button>
        )}
      />
    );
  }
  if (catalogState.status === 'error' || !catalog) {
    return (
      <Alert
        tone="error"
        actions={(
          <button type="button" onClick={() => setCatalogTry((n) => n + 1)} className="font-medium underline">
            Try again
          </button>
        )}
      >
        {catalogState.status === 'error' ? catalogState.message : "The report builder couldn't load. Please try again."}
      </Alert>
    );
  }

  const subject = subjectOf(spec.subject, catalog);
  const measure = measureOf(subject, spec.measure);
  const measureLabel = measure ? measure.label : spec.measure;
  const countsAs = current
    ? current.notes.counts_as || countsAsLine(current.spec, catalog)
    : countsAsLine(spec, catalog);
  const captionOf = (t: PivotTable): string =>
    [measureLabel, t.rowAxis.kind !== 'measure' ? `by ${t.rowAxis.label.toLowerCase()}` : '', t.columnAxis.kind !== 'measure' ? `and ${t.columnAxis.label.toLowerCase()}` : '']
      .filter(Boolean)
      .join(' ');
  const timeChart = !!table && spec.chart === 'bars' && table.columnAxis.kind === 'time';
  const ownerWords = report
    ? report.owner_user_id && report.owner_user_id === userId
      ? 'By you'
      : `By ${report.owner_name || 'a teammate'}`
    : null;

  let body: JSX.Element;
  if (reportLoading) {
    body = (
      <p className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" data-rb="opening">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Opening the report...
      </p>
    );
  } else if (blocked && report) {
    body = (
      <Alert tone="warning" title="This saved report can't run as it is">
        <span data-rb="invalid">{report.invalid}</span>{' '}
        {mayChange
          ? 'Change a setting to fix it, then save it again.'
          : 'Change a setting to run it, then save a copy.'}
      </Alert>
    );
  } else if (problem) {
    body = (
      <p className="py-6 text-sm text-gray-500 dark:text-gray-400" data-rb="problem">
        {`Can't run yet: ${problem}`}
      </p>
    );
  } else if (runFailed) {
    body = (
      <Alert
        tone="error"
        actions={(
          <button type="button" onClick={() => setRunTry((n) => n + 1)} className="font-medium underline">
            Run again
          </button>
        )}
      >
        <span data-rb="run-error">{run.error}</span>
      </Alert>
    );
  } else if (table) {
    const flagged = table.flagged;
    body = (
      <div className={stale ? 'opacity-60 transition-opacity duration-150' : undefined} data-rb="view" data-rb-stale={stale ? 'true' : undefined}>
        {table.grandTotal !== null && (
          <p className="mb-3 flex flex-wrap items-baseline gap-x-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-rb="total">
              {formatValue(table.grandTotal, table.unit)}
            </span>
            <span>{`${measureLabel.toLowerCase()} in total`}</span>
          </p>
        )}
        {timeChart ? (
          <TimeBars table={table} title={captionOf(table)} />
        ) : (
          <ResultTable table={table} bars={spec.chart === 'bars'} caption={captionOf(table)} />
        )}
        {(flagged.other > 0 || flagged.none > 0) && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" data-rb="flag-note">
            {[
              flagged.other > 0 ? '"Other" adds up every group past the ones listed.' : '',
              flagged.none > 0 ? 'A name in brackets, like "(none)", counts the ones with no value for that grouping.' : '',
            ].filter(Boolean).join(' ')}
          </p>
        )}
        {table.empty && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-rb="empty">
            Nothing matched these settings in this date range.
          </p>
        )}
      </div>
    );
  } else {
    body = (
      <p className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" data-rb="running">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Running the report...
      </p>
    );
  }

  return (
    <div
      data-rb="builder"
      className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)] 2xl:grid-cols-[22rem_minmax(0,1fr)]"
    >
      <div className="min-w-0 space-y-4">
        <SavedReports
          scope={scope}
          onScopeChange={changeScope}
          lists={lists}
          openId={report ? report.id : null}
          pinned={pinned}
          onTogglePin={togglePin}
          onOpen={(id) => {
            void onOpen(id);
          }}
          onRetry={refreshLists}
          subjectLabel={subjectLabel}
          userId={userId}
        />
        <SpecPanel
          spec={spec}
          catalog={catalog}
          onChange={onSpecChange}
          studies={studies}
          team={team}
          leadSourceSuggestions={leadSources}
          disabled={reportLoading}
          open={settingsOpen}
          onToggleOpen={() => setSettingsOpen((v) => !v)}
        />
      </div>

      <div className="min-w-0 space-y-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0">
              <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                <span className="min-w-0 truncate" data-rb="title">{report ? report.name : 'New report'}</span>
                {report && (
                  <span data-rb="badges" className="inline-flex flex-wrap items-center gap-1.5">
                    {report.visibility === 'team' ? (
                      <Badge tone="primary" size="sm">Shared with the team</Badge>
                    ) : (
                      <Badge tone="gray" size="sm">Private</Badge>
                    )}
                    {dirty && <Badge tone="amber" size="sm">Unsaved changes</Badge>}
                  </span>
                )}
              </h2>
              {report && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" data-rb="owner">
                  {ownerWords}
                  {report.description ? ` - ${report.description}` : ''}
                </p>
              )}
            </div>
            <ReportActions
              report={report}
              dirty={dirty}
              canChange={mayChange}
              canRun={canRun}
              cannotRunWhy={blocked && report ? report.invalid : problem}
              busy={busy}
              onNew={() => {
                void onNew();
              }}
              onSave={() => {
                void onSave();
              }}
              onSaveAs={() => openDialog('saveAs')}
              onToggleShare={() => {
                void onToggleShare();
              }}
              onCopy={() => {
                void onCopy();
              }}
              onExport={() => {
                void onExport();
              }}
              onArchive={() => {
                void onArchive();
              }}
            />
          </div>
          {reportError && (
            <Alert
              tone="error"
              onDismiss={() => setReportError(null)}
            >
              <span data-rb="report-error">{reportError}</span>
            </Alert>
          )}
          {notice && (
            <Alert tone={notice.tone} size="sm" onDismiss={() => setNotice(null)}>
              <span data-rb="notice">{notice.text}</span>
            </Alert>
          )}
        </div>

        <section
          aria-label="Result"
          aria-busy={running || reportLoading}
          data-rb="result"
          className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <p className="min-w-0 text-xs leading-snug text-gray-500 dark:text-gray-400" data-rb="counts-as">
              {countsAs}
            </p>
            <div className="flex flex-shrink-0 items-center gap-2">
              {running && stale && (
                <span className="text-xs text-gray-500 dark:text-gray-400" data-rb="updating">
                  Updating...
                </span>
              )}
              {canRun && <RefreshButton onClick={() => setRunTry((n) => n + 1)} loading={running} label="Run again" />}
            </div>
          </div>
          {body}
        </section>
      </div>

      {dialog && (
        <SaveReportDialog
          key={dialog.seq}
          title={dialog.mode === 'saveAs' ? 'Save as a new report' : 'Save report'}
          initial={dialog.initial}
          busy={busy === dialog.mode}
          error={dialogError}
          onSubmit={(values) => {
            void submitDialog(values);
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
